<template>
  <div :class="containerClass">
    <page-header
      title="Email Preferences"
      :container-class="null"
      class="mb-32pt"
      infoText="Choose which emails you want to receive. Account, security and enrollment emails are always sent."
    />

    <page-separator title="" />

    <b-skeleton-wrapper :loading="isLoading">
      <template #loading>
        <div class="card card-block card-stretch card-height">
          <div class="card-body">
            <p><b-skeleton width="40%"></b-skeleton></p>
            <p><b-skeleton width="85%"></b-skeleton></p>
            <p><b-skeleton width="85%"></b-skeleton></p>
            <p><b-skeleton width="70%"></b-skeleton></p>
          </div>
        </div>
      </template>

      <b-alert :show="isGloballyUnsubscribed" variant="warning" class="mb-3">
        You are currently unsubscribed from all emails, so nothing below will be delivered. Use the resubscribe link in
        any email we previously sent you to start receiving emails again.
      </b-alert>

      <div v-for="category in categories" :key="category.category" class="card card-block card-stretch card-height">
        <div class="card-header d-flex align-items-center justify-content-between">
          <h5 class="my-0">{{ category.title }}</h5>
          <b-btn
            size="sm"
            variant="link"
            class="p-0"
            :disabled="!!savingKeys.length"
            @click.prevent="toggleCategory(category)"
          >
            {{ isCategoryAllEnabled(category) ? 'Turn all off' : 'Turn all on' }}
          </b-btn>
        </div>
        <div class="card-body">
          <div
            v-for="(email, index) in category.emails"
            :key="email.email_key"
            class="d-flex align-items-start justify-content-between"
            :class="{ 'mb-3': index !== category.emails.length - 1 }"
          >
            <div class="pr-3">
              <div class="form-label mb-0">{{ email.title }}</div>
              <small class="text-muted">{{ email.description }}</small>
            </div>

            <div class="d-flex align-items-center">
              <b-spinner v-if="isSaving(email.email_key)" small class="mr-2" />
              <b-form-checkbox
                switch
                :checked="email.enabled"
                :disabled="!!savingKeys.length"
                @change="value => toggleEmail(email, value)"
              />
            </div>
          </div>
        </div>
      </div>
    </b-skeleton-wrapper>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import Page from '@/components/Page.vue';
import PageHeader from '@/components/Ui/PageHeader.vue';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import { EMAIL_SUBSCRIPTION_STATUS } from '@/common/constants';

export default {
  name: 'ManageEmailPreferences',
  components: {
    PageHeader,
    PageSeparator,
  },
  extends: Page,
  data() {
    return {
      title: 'Email Preferences',
      isLoading: false,
      categories: [],
      subscriptionStatus: EMAIL_SUBSCRIPTION_STATUS.ENABLED,
      savingKeys: [],
    };
  },
  computed: {
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Email Preferences', active: true },
      ];
    },
    isGloballyUnsubscribed() {
      return this.subscriptionStatus === EMAIL_SUBSCRIPTION_STATUS.DISABLED;
    },
  },
  methods: {
    ...mapActions('emailSubscription', ['getEmailPreferences', 'updateEmailPreferences']),

    setPreferences(data) {
      this.categories = data.categories;
      this.subscriptionStatus = data.subscription_status;
    },
    isSaving(emailKey) {
      return this.savingKeys.includes(emailKey);
    },
    isCategoryAllEnabled(category) {
      return category.emails.every(email => email.enabled);
    },

    async fetchPreferences() {
      this.isLoading = true;
      try {
        this.setPreferences(await this.getEmailPreferences());
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },

    async savePreferences(preferences) {
      this.savingKeys = preferences.map(preference => preference.email_key);
      try {
        this.setPreferences(await this.updateEmailPreferences(preferences));
        this.makeToast({ variant: 'success', msg: 'Email preferences updated' });
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        // Re-read from the server so the switches reflect what was actually saved.
        await this.fetchPreferences();
      }
      this.savingKeys = [];
    },

    toggleEmail(email, enabled) {
      this.savePreferences([{ email_key: email.email_key, enabled }]);
    },
    toggleCategory(category) {
      const enabled = !this.isCategoryAllEnabled(category);
      this.savePreferences(category.emails.map(email => ({ email_key: email.email_key, enabled })));
    },
  },

  async mounted() {
    await this.fetchPreferences();
  },
};
</script>
